import React, { useEffect, useContext } from 'react';  
import { useNavigate } from "react-router-dom";
import Axios from "axios";
import { useImmerReducer } from 'use-immer';
import {
    Grid, Snackbar, Typography, Button, TextField, MenuItem,
    FormControlLabel, Checkbox, Alert, Container, Paper, Box, CircularProgress
} from "@mui/material";
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { useTranslation } from 'react-i18next';
import StateContext from "../../Contexts/StateContext";

import {
    areaOptions, AranyosszekOptions, CsikszekOptions, GyergyoszekOptions, KaszonfiuszekOptions, KereszturfiuszekOptions,
    KezdiszekOptions, MarosszekOptions, MiklosvarfiuszekOptions, OrbaiszekOptions, SepsiszekOptions, UdvarhelyszekOptions, CsangofoldOptions,
} from "../../Constants/GeographicConstants";

const defaultIcon = new L.Icon({
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
});

function AddAccommodation() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const GlobalState = useContext(StateContext);

    const config = require('../../config').default;
    const environment = require('../../config').environment;

    const initialState = {
        titleValue: '',
        accommodationTypeValue: '',
        descriptionValue: '',
        areaValue: '',
        boroughValue: '',
        latitudeValue: 45.8643,
        longitudeValue: 25.7848,
        pricePerNightValue: '',
        priceTypeValue: 'Room',
        roomsValue: '',
        capacityValue: '',
        phoneNumberValue: '',
        amenities: {
            wifi: false,
            parking: false,
            pool: false,
            wellness: false,
            air_conditioning: false,
            breakfast: false,
            pets_allowed: false,
            airport_transfer: false,
            vacation_ticket: false,
        },
        uploadedPictures: [],
        addressValue: '',
        geocodingError: '',
        geocodingSuccess: false,
        sendRequest: 0,
        openSnack: false,
        disabledBtn: false,
        error: '',
        // form errors
        titleErrors: { hasError: false, errorMessage: '' },
        typeErrors: { hasError: false, errorMessage: '' },
        areaErrors: { hasError: false, errorMessage: '' },
        boroughErrors: { hasError: false, errorMessage: '' },
        priceErrors: { hasError: false, errorMessage: '' },
    };

    function ourReducer(draft, action) {
        switch (action.type) {
            case 'catchTitleChange':
                draft.titleValue = action.value;
                draft.titleErrors.hasError = false;
                draft.titleErrors.errorMessage = '';
                break;
            case 'catchAccommodationTypeChange':
                draft.accommodationTypeValue = action.value;
                draft.typeErrors.hasError = false;
                draft.typeErrors.errorMessage = '';
                break;
            case 'catchDescriptionChange':
                draft.descriptionValue = action.value;
                break;
            case 'catchAreaChange':
                draft.areaValue = action.value;
                draft.areaErrors.hasError = false;
                draft.areaErrors.errorMessage = '';
                break;
            case 'catchBoroughChange':
                draft.boroughValue = action.value;
                draft.boroughErrors.hasError = false;
                draft.boroughErrors.errorMessage = '';
                break;
            case 'catchLatitudeChange':
                draft.latitudeValue = action.value;
                break;
            case 'catchLongitudeChange':
                draft.longitudeValue = action.value;
                break;
            case 'catchAddressChange':
                draft.addressValue = action.value;
                draft.geocodingError = '';
                draft.geocodingSuccess = false;
                break;
            case 'setGeocodingError':
                draft.geocodingError = action.value;
                draft.geocodingSuccess = false;
                break;
            case 'setGeocodingSuccess':
                draft.geocodingSuccess = true;
                draft.geocodingError = '';
                break;
            case 'catchPriceChange':
                draft.pricePerNightValue = action.value;
                draft.priceErrors.hasError = false;
                draft.priceErrors.errorMessage = '';
                break;
            case 'catchPriceTypeChange':
                draft.priceTypeValue = action.value;
                break;
            case 'catchRoomsChange':
                draft.roomsValue = action.value;
                break;
            case 'catchCapacityChange':
                draft.capacityValue = action.value;
                break;
            case 'catchPhoneNumberChange':
                draft.phoneNumberValue = action.value;
                break;
            case 'catchAmenityChange':
                draft.amenities[action.amenity] = action.value;
                break;
            case 'catchUploadedPictures':
                draft.uploadedPictures = action.value;
                break;
            case 'changeSendRequest':
                draft.sendRequest++;
                break;
            case 'openTheSnack':
                draft.openSnack = true;
                break;
            case 'disableTheBtn':
                draft.disabledBtn = true;
                break;
            case 'enableTheBtn':
                draft.disabledBtn = false;
                break;
            case 'catchTitleErrors':
                draft.titleErrors.hasError = true;
                draft.titleErrors.errorMessage = action.value;
                break;
            case 'catchTypeErrors':
                draft.typeErrors.hasError = true;
                draft.typeErrors.errorMessage = action.value;
                break;
            case 'catchAreaErrors':
                draft.areaErrors.hasError = true;
                draft.areaErrors.errorMessage = action.value;
                break;
            case 'catchBoroughErrors':
                draft.boroughErrors.hasError = true;
                draft.boroughErrors.errorMessage = action.value;
                break;
            case 'catchPriceErrors':
                draft.priceErrors.hasError = true;
                draft.priceErrors.errorMessage = action.value;
                break;
            case 'setError':
                draft.error = action.value;
                break;
            default:
                break;
        }
    }

    const [state, dispatch] = useImmerReducer(ourReducer, initialState);

    function TheMapComponent() {
        const map = useMap();
        useEffect(() => {
            map.on('click', function (e) {
                dispatch({ type: 'catchLatitudeChange', value: e.latlng.lat });
                dispatch({ type: 'catchLongitudeChange', value: e.latlng.lng });
            });
        }, [map]);
        return null;
    }

    const accommodationTypeOptions = [
        { value: 'Camping', label: t('SubMenu.Camping') },
        { value: 'Guesthouse', label: t('SubMenu.Guesthouse') },
        { value: 'Hotel', label: t('SubMenu.Hotel') },
        { value: 'Pension', label: t('SubMenu.Pension') },
        { value: 'Barn', label: t('SubMenu.Barn') },
    ].sort((a, b) => (a.label || '').localeCompare(b.label || ''));

    const priceTypeOptions = [
        { value: 'Room', label: t('Accommodation.PricePerNightRoom') },
        { value: 'Person', label: t('Accommodation.PricePerNightPerson') },
    ];

    const getBoroughOptions = () => {
        switch (state.areaValue) {
            case 'Aranyosszék': return AranyosszekOptions;
            case 'Csíkszék': return CsikszekOptions;
            case 'Gyergyószék': return GyergyoszekOptions;
            case 'Kászonszék': return KaszonfiuszekOptions;
            case 'Keresztúrszék': return KereszturfiuszekOptions;
            case 'Kézdiszék': return KezdiszekOptions;
            case 'Marosszék': return MarosszekOptions;
            case 'Miklósvárszék': return MiklosvarfiuszekOptions;
            case 'Orbaiszék': return OrbaiszekOptions;
            case 'Sepsiszék': return SepsiszekOptions;
            case 'Udvarhelyszék': return UdvarhelyszekOptions;
            case 'Csángóföld': return CsangofoldOptions;
            default: return [];
        }
    };

    const handleGeocode = async () => {
        if (!state.addressValue) return;
        try {
            const response = await Axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(state.addressValue)}`);
            if (response.data && response.data.length > 0) {
                const { lat, lon } = response.data[0];
                dispatch({ type: 'catchLatitudeChange', value: parseFloat(lat) });
                dispatch({ type: 'catchLongitudeChange', value: parseFloat(lon) });
                dispatch({ type: 'setGeocodingSuccess' });
            } else {
                dispatch({ type: 'setGeocodingError', value: t('Thematic.GeocodingNoResult', 'Nem található koordináta a megadott címhez.') });
            }
        } catch (e) {
            console.error("Geocoding error:", e);
            dispatch({ type: 'setGeocodingError', value: t('Thematic.GeocodingError', 'Hiba történt a keresés során.') });
        }
    };

    function FormSubmit(e) {
        e.preventDefault();

        if (!state.titleValue) {
            dispatch({ type: 'catchTitleErrors', value: t('Validation.EmptyTitle') });
            window.scrollTo(0, 0);
            return;
        }
        if (!state.accommodationTypeValue) {
            dispatch({ type: 'catchTypeErrors', value: t('Validation.EmptyType', 'Kérjük válasszon típust!') });
            window.scrollTo(0, 0);
            return;
        }
        if (!state.areaValue) {
            dispatch({ type: 'catchAreaErrors', value: t('Validation.EmptyArea', 'Kérjük válasszon széket!') });
            window.scrollTo(0, 0);
            return;
        }
        if (!state.boroughValue) {
            dispatch({ type: 'catchBoroughErrors', value: t('Validation.EmptyBorough', 'Kérjük válasszon települést!') });
            window.scrollTo(0, 0);
            return;
        }
        if (!state.pricePerNightValue) {
            dispatch({ type: 'catchPriceErrors', value: t('Validation.EmptyPrice', 'Kérjük adjon meg árat!') });
            window.scrollTo(0, 0);
            return;
        }

        dispatch({ type: 'changeSendRequest' });
        dispatch({ type: 'disableTheBtn' });
    }

    useEffect(() => {
        if (state.sendRequest) {
            async function AddAccommodationFunc() {
                const formData = new FormData();
                formData.append('title', state.titleValue);
                formData.append('accommodation_type', state.accommodationTypeValue);
                formData.append('description', state.descriptionValue);
                formData.append('latitude', state.latitudeValue);
                formData.append('longitude', state.longitudeValue);
                formData.append('price_per_night', state.pricePerNightValue);
                formData.append('price_type', state.priceTypeValue);
                formData.append('rooms', state.roomsValue);
                formData.append('capacity', state.capacityValue);
                formData.append('phone_number', state.phoneNumberValue);

                // Add pictures
                for (let i = 0; i < state.uploadedPictures.length; i++) {
                    if (i < 3) {
                        formData.append(`picture${i + 1}`, state.uploadedPictures[i]);
                    }
                }

                try {
                    const headers = {
                        'Content-Type': 'multipart/form-data'
                    };
                    
                    const token = GlobalState.jwtAccessToken || localStorage.getItem('jwtAccessToken');
                    if (token) {
                        headers['Authorization'] = `Bearer ${token}`;
                    }
                    
                    await Axios.post(config[environment].createMarketplaceAccommodationEndpoint, formData, {
                        headers: headers
                    });
                    dispatch({ type: 'openTheSnack' });
                } catch (e) {
                    console.error("Accommodation creation error:", e.response);
                    dispatch({ type: 'enableTheBtn' });
                    dispatch({ type: 'setError', value: t('Common.Error') });
                }
            }
            AddAccommodationFunc();
        }
    }, [state.sendRequest]);

    useEffect(() => {
        if (state.openSnack) {
            setTimeout(() => {
                navigate('/accommodation-market');
            }, 1500);
        }
    }, [state.openSnack]);

    if (!GlobalState.userIsLogged) {
        return (
            <Container maxWidth="sm" sx={{ py: 8 }}>
                <Alert severity="warning">
                    {t('Administration.LoginRequired')}
                </Alert>
                <Button variant="contained" onClick={() => navigate('/login')} sx={{ mt: 2 }}>
                    {t('Administration.LoginButton')}
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="md" sx={{ py: 4, mt: '50px' }}>
            <Paper sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ color: 'var(--sznodarkblue)', fontWeight: 'bold' }}>
                    {t('Accommodation.RegisterAsProvider')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                    {t('Accommodation.RegisterIntro')}
                </Typography>

                <form onSubmit={FormSubmit}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('Common.Title')}
                                value={state.titleValue}
                                onChange={(e) => dispatch({ type: 'catchTitleChange', value: e.target.value })}
                                error={state.titleErrors.hasError}
                                helperText={state.titleErrors.errorMessage}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label={t('Common.Type')}
                                value={state.accommodationTypeValue}
                                onChange={(e) => dispatch({ type: 'catchAccommodationTypeChange', value: e.target.value })}
                                error={state.typeErrors.hasError}
                                helperText={state.typeErrors.errorMessage}
                                required
                            >
                                {accommodationTypeOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('Accommodation.PricePerNightLabel')}
                                value={state.pricePerNightValue}
                                onChange={(e) => dispatch({ type: 'catchPriceChange', value: e.target.value })}
                                error={state.priceErrors.hasError}
                                helperText={state.priceErrors.errorMessage}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <TextField
                                select
                                fullWidth
                                label={t('Accommodation.PriceTypeLabel')}
                                value={state.priceTypeValue}
                                onChange={(e) => dispatch({ type: 'catchPriceTypeChange', value: e.target.value })}
                                required
                            >
                                {priceTypeOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label={t('Map.Toolbox.szek')}
                                value={state.areaValue}
                                onChange={(e) => dispatch({ type: 'catchAreaChange', value: e.target.value })}
                                error={state.areaErrors.hasError}
                                helperText={state.areaErrors.errorMessage}
                                required
                            >
                                {areaOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                select
                                fullWidth
                                label={t('Map.Toolbox.telepules')}
                                value={state.boroughValue}
                                onChange={(e) => dispatch({ type: 'catchBoroughChange', value: e.target.value })}
                                error={state.boroughErrors.hasError}
                                helperText={state.boroughErrors.errorMessage}
                                required
                                disabled={!state.areaValue}
                            >
                                {getBoroughOptions().map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={12} sm={9}>
                            <TextField
                                fullWidth
                                label={t('Common.Address')}
                                value={state.addressValue}
                                onChange={(e) => dispatch({ type: 'catchAddressChange', value: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={handleGeocode}
                                sx={{ height: '56px' }}
                            >
                                {t('Thematic.Geocode')}
                            </Button>
                        </Grid>
                        {state.geocodingError && (
                            <Grid item xs={12}>
                                <Alert severity="error" sx={{ mt: 1 }}>
                                    {state.geocodingError}
                                </Alert>
                            </Grid>
                        )}
                        {state.geocodingSuccess && (
                            <Grid item xs={12}>
                                <Alert severity="success" sx={{ mt: 1 }}>
                                    {t('Thematic.GeocodingSuccess')}
                                </Alert>
                            </Grid>
                        )}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('Academy.Rooms')}
                                value={state.roomsValue}
                                onChange={(e) => dispatch({ type: 'catchRoomsChange', value: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="number"
                                label={t('Business.Capacity')}
                                value={state.capacityValue}
                                onChange={(e) => dispatch({ type: 'catchCapacityChange', value: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t('Common.PhoneNumber', 'Telefonszám')}
                                value={state.phoneNumberValue}
                                onChange={(e) => dispatch({ type: 'catchPhoneNumberChange', value: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                rows={4}
                                label={t('Common.Description')}
                                value={state.descriptionValue}
                                onChange={(e) => dispatch({ type: 'catchDescriptionChange', value: e.target.value })}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>{t('Amenities.Title', 'Felszereltség')}</Typography>
                            <Grid container>
                                {Object.keys(state.amenities).map((amenity) => (
                                    <Grid item xs={12} sm={4} key={amenity}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={state.amenities[amenity]}
                                                    onChange={(e) => dispatch({ type: 'catchAmenityChange', amenity, value: e.target.checked })}
                                                />
                                            }
                                            label={t(`Amenities.${amenity.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join('')}`)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>{t('Common.Location')}</Typography>
                            <Box sx={{ height: 300, width: '100%', mb: 2 }}>
                                <MapContainer center={[state.latitudeValue, state.longitudeValue]} zoom={13} scrollWheelZoom={true} style={{ height: '100%', width: '100%' }}>
                                    <TileLayer url={config[environment].osmTiles} />
                                    <TheMapComponent />
                                    <Marker position={[state.latitudeValue, state.longitudeValue]} icon={defaultIcon} />
                                </MapContainer>
                            </Box>
                            <Typography variant="caption">
                                {t('Common.MapClickHint', 'Kattintson a térképre a pontos helyszín megjelöléséhez!')}
                            </Typography>
                        </Grid>

                        <Grid item xs={12}>
                            <Typography variant="h6" gutterBottom>{t('Common.Pictures', 'Képek feltöltése')}</Typography>
                            <Button variant="contained" component="label">
                                {t('Common.UploadPictures', 'Képek kiválasztása')}
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => dispatch({ type: 'catchUploadedPictures', value: Array.from(e.target.files) })}
                                />
                            </Button>
                            <Box sx={{ mt: 1 }}>
                                {state.uploadedPictures.map((file, index) => (
                                    <Typography key={index} variant="body2">{file.name}</Typography>
                                ))}
                            </Box>
                        </Grid>
                    </Grid>

                    {state.error && <Alert severity="error" sx={{ mt: 3 }}>{state.error}</Alert>}

                    <Box sx={{ mt: 4, display: 'flex', gap: 2 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            size="large"
                            disabled={state.disabledBtn}
                            sx={{ bgcolor: 'var(--sznodarkblue)' }}
                        >
                            {state.disabledBtn ? <CircularProgress size={24} color="inherit" /> : t('Common.Submit', 'Regisztráció')}
                        </Button>
                        <Button variant="outlined" size="large" onClick={() => navigate(-1)}>
                            {t('Common.Cancel', 'Mégse')}
                        </Button>
                    </Box>
                </form>
            </Paper>

            <Snackbar open={state.openSnack} autoHideDuration={3000} onClose={() => dispatch({ type: 'openTheSnack', value: false })}>
                <Alert severity="success" sx={{ width: '100%' }}>
                    {t('Accommodation.RegisterSuccess', 'Sikeresen regisztrálta szálláshelyét!')}
                </Alert>
            </Snackbar>
        </Container>
    );
}

export default AddAccommodation;
